/**
 * FFoolscapContent - Clean canvas view of the Foolscap document
 *
 * This component ALWAYS shows the clean document (no annotations).
 * Annotations are shown in the separate Edits tab (FEditsPanel).
 * This matches the Zinsser "clean canvas + edits tab" pattern.
 *
 * Uses dangerouslySetInnerHTML with marked for consistent rendering
 * of card: protocol links (TipTap strips custom protocols).
 */

import { useEffect, useRef, useMemo, useCallback } from 'react'
import { marked } from 'marked'

interface FFoolscapContentProps {
  foolscapMarkdown: string | null
  streaming?: boolean
  onCardClick?: (cardId: string) => void
}

// Configure marked
marked.use({
  breaks: true,
  gfm: true,
})

export function FFoolscapContent({
  foolscapMarkdown,
  streaming = false,
  onCardClick,
}: FFoolscapContentProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Convert markdown to HTML
  const htmlContent = useMemo(() => {
    if (!foolscapMarkdown) return ''
    return marked.parse(foolscapMarkdown, { async: false }) as string
  }, [foolscapMarkdown])

  // Handle clicks on card links (card:UUID format)
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const link = target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    // Check for card: protocol
    if (href.startsWith('card:')) {
      e.preventDefault()
      e.stopPropagation()
      const cardId = href.slice(5) // Remove 'card:' prefix
      if (cardId && onCardClick) {
        onCardClick(cardId)
      }
    }
  }, [onCardClick])

  // Highlight last block during streaming
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    if (!streaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
    }

    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [streaming, htmlContent])

  if (!foolscapMarkdown) return null

  // Render clean content with dangerouslySetInnerHTML (preserves card: links)
  return (
    <div className="px-4 pt-3 pb-20 md:pb-[30vh]">
      <div
        className="prose-z min-h-full"
        ref={containerRef}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </div>
  )
}
